import { memo, useState } from 'react'
import { Box, Typography, Skeleton, Button, Chip } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import {
  Warning as WarningIcon,
  Article as CertificateIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material'
import { AddIcon } from 'src/v3/core/icons'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import {
  selectActiveLinkedService,
  selectSelectedUser,
  fetchSelectedUserCertificates,
} from 'src/v3/manage/manage.slice'
import { useNotifications } from '../../hooks/use-notifications'
import ExternalCertificateModal from './modals/external-certificate-modal'

interface CertificatesDataGridProps {
  transformedCertificates: any[]
  certificateColumns: GridColDef[]
  selectedUserCertificatesStatus: string
  CustomToolbar: React.ComponentType
}

export let CertificatesDataGrid = memo(
  ({
    transformedCertificates,
    certificateColumns,
    selectedUserCertificatesStatus,
    CustomToolbar,
  }: CertificatesDataGridProps) => {
    let dispatch = useAppDispatch()
    let { showSuccess, showError } = useNotifications()

    let [isExternalCertModalOpen, setIsExternalCertModalOpen] = useState(false)

    let selectedUser = useAppSelector(selectSelectedUser)
    let linkedServiceID = useAppSelector(selectActiveLinkedService)

    let totalRecorded = transformedCertificates.filter(
      (cert) => cert.type === 'recorded',
    ).length
    let totalLive = transformedCertificates.filter(
      (cert) => cert.type === 'live',
    ).length
    let totalExternal = transformedCertificates.filter(
      (cert) => cert.type === 'external',
    ).length

    return (
      <>
        <Box>
          <Box className="px-6 mb-6">
            <Box className="flex flex-wrap items-center justify-between mb-2">
              <Typography
                variant="h6"
                component="h2"
                className="flex items-center gap-2 font-semibold"
                id="certificates-section-heading"
              >
                <ExportIcon aria-hidden="true" />
                Certificates & Learning Records
              </Typography>
              <Box className="flex flex-wrap items-center gap-2">
                {totalRecorded > 0 && (
                  <Chip
                    label={`${totalRecorded} Recorded`}
                    size="medium"
                    variant="outlined"
                    color="primary"
                  />
                )}
                {totalLive > 0 && (
                  <Chip
                    label={`${totalLive} Live`}
                    size="medium"
                    variant="outlined"
                    color="error"
                  />
                )}
                {totalExternal > 0 && (
                  <Chip
                    label={`${totalExternal} External`}
                    size="medium"
                    variant="outlined"
                    color="secondary"
                  />
                )}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => setIsExternalCertModalOpen(true)}
                  aria-label="Add new external certificate"
                  className="whitespace-nowrap basis-full md:basis-auto"
                >
                  Add External Certificate
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              All learning achievements for this user. Use the toolbar to
              filter, search, and export to CSV format.
            </Typography>
          </Box>

          {selectedUserCertificatesStatus === 'rejected' ? (
            <Box
              className="grid py-12 place-items-center"
              role="alert"
              aria-labelledby="certificates-section-heading"
            >
              <Box className="text-center" sx={{ color: 'text.secondary' }}>
                <WarningIcon
                  className="mb-4 opacity-50"
                  sx={{
                    fontSize: 64,
                    color: 'warning.main',
                  }}
                  aria-hidden="true"
                />
                <Typography variant="h6" className="mb-2">
                  Failed to Load Certificates
                </Typography>
                <Typography variant="body2">
                  There was an error loading the certificate data. Please try
                  refreshing the page.
                </Typography>
              </Box>
            </Box>
          ) : transformedCertificates.length === 0 ? (
            <Box
              className="grid py-12 place-items-center"
              role="status"
              aria-labelledby="certificates-section-heading"
            >
              <Box className="text-center" sx={{ color: 'text.secondary' }}>
                <CertificateIcon
                  className="mb-4 opacity-50"
                  sx={{ fontSize: 64 }}
                  aria-hidden="true"
                />
                <Typography variant="h6" className="mb-2">
                  No Certificates Found
                </Typography>
                <Typography variant="body2">
                  No learning achievements found for this user.
                </Typography>
              </Box>
            </Box>
          ) : selectedUserCertificatesStatus === 'pending' ? (
            <Skeleton
              variant="rectangular"
              height={500}
              sx={{ borderRadius: 1 }}
              aria-label="Loading certificates data"
            />
          ) : (
            <Box
              className="w-full h-full"
              sx={{ height: 500 }}
              role="region"
              aria-labelledby="certificates-section-heading"
            >
              <DataGridPremium
                rows={transformedCertificates}
                columns={certificateColumns}
                slots={{ toolbar: CustomToolbar }}
                showToolbar
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                  sorting: {
                    sortModel: [{ field: 'completionDate', sort: 'desc' }],
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                checkboxSelection
                disableRowSelectionOnClick
                aria-label="User certificates and learning records"
                getRowId={(row) => row.id}
                sx={{
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderRadius: 1,
                  borderColor: 'divider',
                  '& .MuiDataGrid-toolbarContainer': {
                    borderBottom: 1,
                    borderColor: 'divider',
                    p: 2,
                    backgroundColor: 'background.default',
                  },
                  '& .MuiDataGrid-cell': {
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'background.paperDark',
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderColor: 'divider',
                    backgroundColor: 'background.paperDark',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'background.default',
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <ExternalCertificateModal
          isOpen={isExternalCertModalOpen}
          userID={selectedUser!.id}
          linkedServiceID={linkedServiceID!}
          onClose={() => {
            setIsExternalCertModalOpen(false)
          }}
          onSuccess={() => {
            dispatch(
              fetchSelectedUserCertificates({
                id: selectedUser!.id,
                linkedServiceID: linkedServiceID!,
              }),
            )
            showSuccess('External Certificate Added Successfully')
          }}
          onFailure={() => showError('Error Adding External Certificate')}
        />
      </>
    )
  },
)

export default CertificatesDataGrid
